import React, { useState } from 'react';
import { Box, Typography, Button, Chip, TextField } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAssignmentService } from '../services/assignmentService';
import { useLearnService } from '../services/learnService';
import { EmployeeType, useEmployeeService } from '../services/employeeService';
import OrgChartTree from '../components/EmployeeOrgChartTree';
import Autocomplete from '@mui/material/Autocomplete';
import { AssessmentType } from '../services/assessmentService';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';



// --- EmployeeSearchAutocomplete ---
const EmployeeSearchAutocomplete: React.FC<{
  onSelect: (employee: EmployeeType) => void;
}> = ({ onSelect }) => {
  const employeeService = useEmployeeService();
  const [options, setOptions] = useState<EmployeeType[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const fetch = async () => {
      if (input.length < 5) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const employees = await employeeService.searchEmployees(input);
        setOptions(employees);
      } catch (e) {
        console.log(`AdminAssignScenarioTab: Error while searching employee - ${e}`);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };
    const handle = setTimeout(fetch, 300);
    return () => clearTimeout(handle);
  }, [input]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={opt => opt.employee_email}
      loading={loading}
      onInputChange={(_, value) => setInput(value)}
      onChange={(_, value) => {
        if (value) {
          onSelect(value);
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label="Search for employee by email"
          helperText="Type at least 5 characters to search"
        />
      )}
    />
  );
};

const AssignScenarioTab: React.FC<{ assessment: AssessmentType }> = ({ assessment }) => {
  const assignmentService = useAssignmentService();
  const learnService = useLearnService();
  const [dateRange, setDateRange] = useState<
    { startDate: Dayjs | null, endDate: Dayjs | null, key: string }[]
  >([{ startDate: dayjs(), endDate: dayjs(), key: 'selection' }]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccessMessage, setAssignSuccessMessage] = useState<string | null>(null);

  const handleUserSelect = (employee: EmployeeType & { selectTree?: string[], deselectTree?: string[] }) => {
    if (employee.selectTree) {
      setSelectedUsers(prev =>
        Array.from(new Set([...prev, ...employee.selectTree!]))
      );
    } else if (employee.deselectTree) {
      setSelectedUsers(prev =>
        prev.filter(email => !employee.deselectTree!.includes(email))
      );
    } else if (employee.employee_email) {
      setSelectedUsers(prev =>
        prev.includes(employee.employee_email)
          ? prev.filter(email => email !== employee.employee_email)
          : [...prev, employee.employee_email]
      );
    }
  };

  const handleAssign = async () => {
    if (!dateRange[0].startDate || !dateRange[0].endDate) {
      setAssignError("Please select both a start and end date.");
      return;
    }

    const requestBody = {
      scenario_id: Number(assessment.id),
      emails: selectedUsers,
      start_date: dateRange[0].startDate.toISOString(),
      end_date: dateRange[0].endDate.toISOString(),
    };
    setAssignLoading(true);
    setAssignError(null);
    setAssignSuccessMessage(null);
    try {
      const result = await assignmentService.assignScenario(requestBody);
      const skipped = result.skipped ?? 0;
      const updatedDoceboUserIds = result.updated_docebo_user_id ?? 0;
      const parts = [`${result.created} assignment${result.created === 1 ? '' : 's'} created`];
      if (skipped > 0) {
        parts.push(`${skipped} already assigned`);
      }
      if (updatedDoceboUserIds > 0) {
        parts.push(`${updatedDoceboUserIds} Docebo user ID${updatedDoceboUserIds === 1 ? '' : 's'} updated`);
      }
      setAssignSuccessMessage(parts.join(', ') + '.');
      setSelectedUsers([]);
    } catch (err: any) {
      setAssignError(err.message || 'Unknown error');
    } finally {
      setAssignLoading(false);
    }
  };

  const listEnrolledUsers = async () => {
    try {
      const enrolledUsers = await learnService.listEnrolledUsers(Number(assessment.courseId));
      console.log('Enrolled users fetched:', enrolledUsers);
      const courseDates = enrolledUsers.find(user => user.courseDateStart || user.courseDateEnd);
      if (courseDates) {
        setDateRange(prev => [{
          ...prev[0],
          startDate: courseDates.courseDateStart ? dayjs(courseDates.courseDateStart) : prev[0].startDate,
          endDate: courseDates.courseDateEnd ? dayjs(courseDates.courseDateEnd) : prev[0].endDate,
          key: 'selection',
        }]);
      }

      const enrolledUserEmails = enrolledUsers
        .map(user => user.email)
        .filter((email): email is string => Boolean(email));

      if (enrolledUserEmails.length === 0) {
        setAssignError('No enrolled users with email addresses were found.');
        return;
      }

      setSelectedUsers(Array.from(new Set(enrolledUserEmails)));
      setAssignError(null);
    } catch (err: any) {
      console.error('Error fetching enrolled users:', err);
      setAssignError(err.message || 'Failed to fetch enrolled users.');
    }
  };

  return (
    <Box>
      <Box sx={{ bgcolor: 'transparent', p: 2, borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2}}>
            <InfoOutlinedIcon color="primary" fontSize="large" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {assessment?.title}
              </Typography>
              <Typography variant="body2">
                {assessment?.description}
              </Typography>
            </Box>
          </Box>
          <Box sx={{display: 'flex', gap: 2}}>
            <Button variant="outlined" size="small" sx={{ ml: 'auto' }}>
              Duration: {assessment?.duration ?? 'N/A'}
            </Button>
            {assessment.courseId && (
              <Button variant="outlined" size="small" sx={{ ml: 'auto' }}
                onClick={listEnrolledUsers}
              >
                Sync assignments from SHI learn
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      
      {assignError && (
        <Box mb={2}><Typography color="error">{assignError}</Typography></Box>
      )}
      {assignSuccessMessage && (
        <Box mb={2}><Typography color="success.main">{assignSuccessMessage}</Typography></Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          mb: 3,
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' }
        }}
      >
        {/* Calendar column */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            minWidth: 0,
            width: '100%',
            overflow: 'auto',
            '.rdrCalendarWrapper': { width: '100%' }
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={['DatePicker']} sx={{
              display:'flex',
              justifyContent: 'space-around',
              width: '100%'
            }}>
              <DatePicker
                label="Start Date"
                value={dateRange[0].startDate}
                onChange={newValue => {
                  setDateRange([{ ...dateRange[0], startDate: newValue, key: 'selection' }]);
                }}
              />
              <DatePicker
                label="End Date"
                value={dateRange[0].endDate}
                onChange={newValue => {
                  setDateRange([{ ...dateRange[0], endDate: newValue, key: 'selection' }]);
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 3,
            mb: 3,
            alignItems: 'flex-start',
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}
        >
          {/* User controls column */}
          <Box sx={{ flex: 1.2, minWidth: 260, mr: 2 }}>
            <EmployeeSearchAutocomplete
              onSelect={handleUserSelect}
            />
          </Box>
          {/* Org Chart Tree column */}
          <Box sx={{ flex: 1.5, maxHeight:400, flexWrap:'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Browse Org Chart
            </Typography>
            <OrgChartTree
              onSelect={handleUserSelect}
              selectedUsers={selectedUsers}
            />
          </Box>
        </Box>
          {selectedUsers.length > 0 && (
              <Box mt={2} p={1} sx={{ borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Selected Users:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedUsers.map(user => (
                    <Chip
                      key={user}
                      label={user}
                      color="primary"
                      onDelete={() => setSelectedUsers(selectedUsers.filter(u => u !== user))}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
          )}
        
      </Box>
      <Box mt={5} display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" color="secondary" onClick={() => {
          setSelectedUsers([]);
        }} disabled={assignLoading}>
          Clear
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAssign}
          disabled={assignLoading || selectedUsers.length === 0}
        >
          {assignLoading ? 'Assigning...' : 'Assign'}
        </Button>
      </Box>
    </Box>
  );
};

export default AssignScenarioTab;